
import { supabase } from '@/integrations/supabase/client';

export const teamPhotoService = {
  async uploadTeamLogo(file: File, fileName: string): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      console.log('Starting team logo upload...', { fileName });
      
      // Get current user from both auth sources with better error handling
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.warn('Supabase auth error:', authError);
      }
      
      // Get internal user data - check session storage first
      let internalUserId = null;
      try {
        // First check internal_user_session which is where useInternalAuth stores the user
        const sessionData = localStorage.getItem('internal_user_session');
        if (sessionData) {
          console.log('Found internal_user_session:', sessionData);
          const parsed = JSON.parse(sessionData);
          if (parsed && parsed.user && parsed.user.id) {
            internalUserId = parsed.user.id;
            console.log('Got internal user ID from session:', internalUserId);
          }
        }
        
        // Fallback to old internal_user format
        if (!internalUserId) {
          const internalUserData = localStorage.getItem('internal_user');
          if (internalUserData) {
            const userData = JSON.parse(internalUserData);
            internalUserId = userData.id;
            console.log('Got internal user ID from fallback:', internalUserId);
          }
        }
      } catch (e) {
        console.warn('Internal user parsing failed, trying fallback:', e);
        internalUserId = localStorage.getItem('internal_user_id');
      }
      
      console.log('Auth status:', { 
        supabaseUser: !!user, 
        supabaseUserId: user?.id, 
        internalUserId,
        hasAnyAuth: !!(user?.id || internalUserId)
      });
      
      // Use the appropriate user ID - prioritize Supabase auth, fallback to internal
      const userId = user?.id || internalUserId;
      if (!userId) {
        console.error('No valid user ID found from either auth system');
        return { 
          success: false, 
          error: 'Authentication required. Please log in to upload team logos.' 
        };
      }
      
      console.log('Uploading team logo for user:', userId);

      // Ensure file is valid
      if (!file || file.size === 0) {
        return { success: false, error: 'Invalid file provided' };
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return { 
          success: false, 
          error: 'Invalid file type. Please upload JPEG, PNG, or WebP images.' 
        };
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        return { success: false, error: 'File too large. Maximum size is 5MB.' };
      }

      // Upload to the 'team-assets' bucket
      const bucketName = 'team-assets';
      const fileExt = file.type.split('/')[1] || 'jpg';
      const filePath = `logos/${userId}/${fileName}.${fileExt}`;
      
      console.log('Uploading to path:', filePath);
      
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (error) {
        console.error('Storage upload error:', error);
        return { 
          success: false, 
          error: `Upload failed: ${error.message}` 
        };
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        console.error('Failed to get public URL');
        return { 
          success: false, 
          error: 'Upload succeeded but failed to generate public URL' 
        };
      }

      console.log('Team logo uploaded successfully:', urlData.publicUrl);
      
      // Track activity (fire-and-forget)
      import('./activityTrackingService').then(({ activityTrackingService }) => {
        activityTrackingService.recordPhotoUpload(userId, 'team').catch(err =>
          console.error('Failed to track team logo upload activity:', err)
        );
      });
      
      return { success: true, url: urlData.publicUrl };

    } catch (error) {
      console.error('Unexpected team logo upload error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to upload team logo' 
      };
    }
  },

  async uploadTeamPhoto(file: File, fileName: string): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      console.log('Starting team photo upload...', { fileName });
      
      // Get current user from both auth sources
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get internal user data - check session storage first
      let internalUserId = null;
      try {
        // First check internal_user_session which is where useInternalAuth stores the user
        const sessionData = localStorage.getItem('internal_user_session');
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (parsed && parsed.user && parsed.user.id) {
            internalUserId = parsed.user.id;
          }
        }
        
        // Fallback to old internal_user format
        if (!internalUserId) {
          const internalUserData = localStorage.getItem('internal_user');
          if (internalUserData) {
            const userData = JSON.parse(internalUserData);
            internalUserId = userData.id;
          }
        }
      } catch (e) {
        // Fallback to old method
        internalUserId = localStorage.getItem('internal_user_id');
      }
      
      // Use the appropriate user ID - prioritize Supabase auth, fallback to internal
      const userId = user?.id || internalUserId;
      if (!userId) {
        console.error('No valid user ID found');
        return { success: false, error: 'You must be logged in to upload team photos' };
      }
      
      console.log('Uploading team photo for user:', userId);

      // Upload to the 'team-assets' bucket 
      const bucketName = 'team-assets';
      const filePath = `photos/${userId}/${fileName}.jpg`;
      
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('Upload error:', error);
        return { success: false, error: error.message };
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log('Team photo uploaded successfully:', urlData.publicUrl);
      
      // Track activity (fire-and-forget)
      import('./activityTrackingService').then(({ activityTrackingService }) => {
        activityTrackingService.recordPhotoUpload(userId, 'team').catch(err =>
          console.error('Failed to track team photo upload activity:', err)
        );
      });
      
      return { success: true, url: urlData.publicUrl };

    } catch (error) {
      console.error('Team photo upload error:', error);
      return { success: false, error: 'Failed to upload team photo' };
    }
  }
};
